/**
 * The app's one routine poll: GET /api/v2/sync/summary.
 *
 * Instead of every badge and list asking its own endpoint on its own timer,
 * one shared poll (createPoller: paused while the tab is hidden, backing off
 * while nothing changes) asks the server for counts and short version hashes:
 *
 *   notifications   { unread }            the bell badge
 *   clientMessages  { unread, version }   client texts nobody has read
 *   asks            { open, version }     Bailey waiting on a person
 *   concierge       { needs, version }    conversations flagged "needs you"
 *   conciergeInbox  { version }           the receptionist inbox's newest rows
 *   reception       { version }           this device's reception-mode settings
 *   diary           { version, ... }      one diary window (asked for on tab return)
 *   campaigns       { version }           where the newest campaigns are up to
 *
 * Screens subscribe to the areas they show and refetch their real endpoint
 * only when that area's version moves (or on a user action). An unchanged
 * answer is a bodyless 304 (If-None-Match).
 *
 * OLDER SERVER: if /sync/summary isn't there (404 — the web app can ship
 * before the API), this falls back to the old cadence per area by reporting a
 * "legacy" change when that area's old interval has passed, so each screen
 * fetches its old endpoint exactly as before. Rechecked every 30 minutes.
 * Three failed requests in a row (offline, a proxy refusing it) do the same,
 * so a badge never freezes because the summary can't be reached.
 */
import axios from 'axios';
import API from '../config/API';
import { createPoller } from './poller';

export const DEFAULT_CADENCE = { base: 30 * 1000, max: 120 * 1000 };
// The intervals each area polled at before /sync/summary (legacy fallback).
const LEGACY_MS = {
  notifications: 5 * 60 * 1000,
  reception: 60 * 1000,
  asks: 2 * 60 * 1000,
  concierge: 2 * 60 * 1000,
  clientMessages: 2 * 60 * 1000,
  conciergeInbox: 20 * 1000,
  campaigns: 20 * 1000,
  diary: 0, // asked for on tab return only: always "changed"
};
const UNSUPPORTED_RECHECK_MS = 30 * 60 * 1000;
const MAX_FAILURES = 3;
let failures = 0;

let data = {};           // area -> latest answer (null = not available to this person)
let etag = null;
let etagQuery = null;
let unsupportedUntil = 0;
const legacyAt = {};
const subs = new Set();
let poller = null;

function _read(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }

function _headers() {
  return {
    'Content-Type': 'application/json',
    authorization: 'Basic YmV1YXBwOmJldWFwcA==',
    accessToken: `${_read('accessToken')}`,
  };
}

function _excludeSensitive() {
  try {
    const s = JSON.parse(_read('receptionModeSettings') || 'null');
    return !!(s && s.enabled);
  } catch (e) { return false; }
}

/** What this run asks for: every subscriber's areas (+ tab-return-only ones). */
function _wanted(reason) {
  const areas = new Set();
  const params = {};
  subs.forEach((s) => {
    s.areas.forEach((a) => areas.add(a));
    if (reason === 'visible' && s.visibleAreas.length) {
      s.visibleAreas.forEach((a) => areas.add(a));
      Object.assign(params, s.params ? s.params() || {} : {});
    }
  });
  return { areas: [...areas].sort(), params };
}

function _emit(event) {
  subs.forEach((s) => {
    const mine = s.areas.concat(event.reason === 'visible' ? s.visibleAreas : []);
    if (!mine.some((a) => event.areas.includes(a))) return;
    try { s.onUpdate(data, event); } catch (e) { /* a screen's handler never stops the poll */ }
  });
}

function _legacy(reason, areas) {
  const now = Date.now();
  const changed = [];
  areas.forEach((a) => {
    const every = LEGACY_MS[a] || 0;
    if (!legacyAt[a] || every === 0 || now - legacyAt[a] >= every || reason === 'poke' || reason === 'start') {
      legacyAt[a] = now;
      data[a] = { legacy: true, version: `legacy:${now}` };
      changed.push(a);
    }
  });
  _emit({ reason, areas, changed, legacy: true });
  return changed.length > 0;
}

let lastToken = null;

async function run(reason) {
  const token = _read('accessToken');
  if (!token) return false;
  // Signed in as someone else: nothing from the last person carries over.
  if (token !== lastToken) { data = {}; etag = null; etagQuery = null; lastToken = token; }
  const { areas, params } = _wanted(reason);
  if (!areas.length) return false;
  if (Date.now() < unsupportedUntil) return _legacy(reason, areas);

  const query = Object.assign({ areas: areas.join(',') }, params, _excludeSensitive() ? { excludeSensitive: 'true' } : {});
  const qk = JSON.stringify(query);
  const headers = _headers();
  if (etag && etagQuery === qk) headers['If-None-Match'] = etag;

  let res;
  try {
    res = await axios.get(API.SYNC_SUMMARY, {
      headers,
      params: query,
      validateStatus: (s) => (s >= 200 && s < 300) || s === 304 || s === 404,
    });
  } catch (e) {
    // Three failures in a row (offline, or a proxy refusing the request):
    // don't leave badges frozen — use the old endpoints for a while.
    if (++failures >= MAX_FAILURES) {
      failures = 0;
      unsupportedUntil = Date.now() + UNSUPPORTED_RECHECK_MS;
      return _legacy(reason, areas);
    }
    _emit({ reason, areas, changed: [], error: true });
    return false;
  }
  failures = 0;
  if (res.status === 404) {
    unsupportedUntil = Date.now() + UNSUPPORTED_RECHECK_MS;
    return _legacy(reason, areas);
  }
  if (res.status === 304) {
    _emit({ reason, areas, changed: [] });
    return false;
  }
  const body = res.data;
  if (!body || body.statusCode !== 1) {
    _emit({ reason, areas, changed: [], error: true });
    return false;
  }
  const next = body.responseData || {};
  const changed = areas.filter((a) => !(a in data) || JSON.stringify(next[a] ?? null) !== JSON.stringify(data[a] ?? null));
  areas.forEach((a) => { data[a] = next[a] === undefined ? null : next[a]; });
  const tag = res.headers && (res.headers.etag || res.headers.ETag);
  etag = tag || null;
  etagQuery = tag ? qk : null;
  _emit({ reason, areas, changed });
  return changed.length > 0;
}

function _cadence() {
  let base = DEFAULT_CADENCE.base;
  let max = DEFAULT_CADENCE.max;
  subs.forEach((s) => {
    if (s.cadence && s.cadence.base) base = Math.min(base, s.cadence.base);
    if (s.cadence && s.cadence.max) max = Math.min(max, s.cadence.max);
  });
  return { base, max: Math.max(base, max) };
}

function _sync() {
  if (subs.size && !poller) {
    poller = createPoller({ key: 'sync-summary', run, ...DEFAULT_CADENCE });
    poller.setCadence(_cadence());
    poller.start();
  } else if (!subs.size && poller) {
    poller.stop();
    poller = null;
  } else if (poller) {
    poller.setCadence(_cadence());
  }
}

/**
 * Follow some areas. Returns an unsubscribe.
 *   areas         asked for on every poll
 *   visibleAreas  asked for only when the tab comes back (the diary)
 *   params()      extra query for those (diaryStart / diaryEnd)
 *   cadence       { base, max } while subscribed (the fastest subscriber wins)
 *   onUpdate(data, { reason, areas, changed, error?, legacy? })  after each answer
 */
export function subscribe({ areas = [], visibleAreas = [], params = null, cadence = null, onUpdate }) {
  const sub = { areas: areas.slice(), visibleAreas: visibleAreas.slice(), params, cadence, onUpdate: onUpdate || (() => {}) };
  const fresh = sub.areas.some((a) => !(a in data));
  const hadPoller = !!poller;
  subs.add(sub);
  _sync();
  // A screen that needs an area nobody asked for yet gets it now, not at the
  // end of a backed-off wait. (A new poller asks on start anyway.)
  if (fresh && hadPoller) poller.poke();
  const unsubscribe = () => { subs.delete(sub); _sync(); };
  /** Change this subscription's cadence (e.g. something started sending). */
  unsubscribe.setCadence = (next) => { sub.cadence = next || null; _sync(); };
  return unsubscribe;
}

/** A user action: ask now and go back to the base interval. */
export function poke() { return poller ? poller.poke() : Promise.resolve(false); }

/** The latest answer for an area: undefined = not asked yet, null = not available. */
export function getArea(area) { return data[area]; }

/** A version that means "I loaded this" — for legacy answers there is none to trust. */
export function versionOf(value) {
  return value && !value.legacy && value.version ? value.version : null;
}

// Tests only.
export function _resetSync() {
  if (poller) poller.stop();
  poller = null;
  subs.clear();
  data = {};
  etag = null;
  etagQuery = null;
  lastToken = null;
  failures = 0;
  unsupportedUntil = 0;
  Object.keys(legacyAt).forEach((k) => { delete legacyAt[k]; });
}
